/**
 * @file   PropertySelectorDropdown.js
 * @brief  Compact property selector for Match tab - replaces MemberProperties/AgentProperties
 *         with a unified dropdown card that shows full address + asking price
 */
import React, { useState, useRef, useEffect } from "react";
import { getAddressDisplay } from "../../../helpers/common";
import { formatCompactPrice } from "../../../helpers/priceCapUtils";
import { BUY, LONG_RENT } from "../../../constants/common";
import noImage from "../../../images/no-image-placeholder.png";
import PropertyDropdownList from "./PropertyDropdownList";

const PropertySelectorDropdown = ({
  propertyList,
  selectedProperty,
  setSelectedProperty,
  adType,
  userType,
  selectedClient,
  userDetails,
  updateReloadParam,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!propertyList || !selectedProperty) return null;

  const addr = getAddressDisplay(
    selectedProperty.Location,
    selectedProperty.City,
    selectedProperty.State,
    selectedProperty.ZIP,
  );

  // Pick the right asking price based on ad type
  const askingPrice =
    adType === BUY
      ? selectedProperty.AskingPrice_Sell
      : adType === LONG_RENT
        ? selectedProperty.AskingPrice_Rent
        : selectedProperty.AskingPrice_Night;

  return (
    <div className="property-selector-dropdown" ref={wrapperRef}>
      <button
        className={`property-selector-card ${isOpen ? "property-selector-card--open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <div className="property-selector-card__thumb">
          <img
            src={selectedProperty.photo || noImage}
            alt=""
          />
        </div>
        <div className="property-selector-card__info">
          <span className="property-selector-card__address-line1">
            {addr.before || selectedProperty.Location}
          </span>
          {addr.after && (
            <span className="property-selector-card__address-line2">
              {addr.after}
            </span>
          )}
        </div>
        <div className="property-selector-card__meta">
          {askingPrice > 0 && (
            <span className="property-selector-card__price">
              {formatCompactPrice(askingPrice)}
            </span>
          )}
          <span className={`property-selector-card__chevron ${isOpen ? "property-selector-card__chevron--up" : ""}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </button>

      {isOpen && (
        <PropertyDropdownList
          propertyList={propertyList}
          selectedProperty={selectedProperty}
          setSelectedProperty={(property) => {
            setSelectedProperty(property);
            setIsOpen(false);
          }}
          adType={adType}
          userType={userType}
          selectedClient={selectedClient}
          userDetails={userDetails}
          updateReloadParam={updateReloadParam}
        />
      )}
    </div>
  );
};

export default PropertySelectorDropdown;
